/**
 * Suites à traiter — la file unique des suites à donner nées à la validation d'un bon : liste à gauche, lecture de la
 * suite à droite (lot I2.1, ADR-0014).
 *
 * Même mise en page que « Gammes », Le Parc et « Interventions » (retour de Benjamin, 2026-10-08) : chaque colonne
 * défile à la molette indépendamment de l'autre ; l'en-tête de la suite — son type, ses boutons et les messages du
 * dernier geste — reste collé en haut de la colonne de droite, là où l'on a cliqué ; une recherche en tête de liste ;
 * des filtres rapides d'état (« À traiter » d'abord) et de type. L'état — recherche, filtres, suite lue, machine
 * filtrée — est porté par l'URL : l'écran se copie, se partage et survit à un rechargement.
 *
 * Deux gestes, tous deux DÉFINITIFS (le serveur interdit toute réouverture) et tous deux avec une RAISON obligatoire :
 * « Écarter » (elle ne sert plus à rien) et « Clore » (c'est fait). Aucun devis, aucune visite ni aucune commande n'est
 * créé : ces modules n'existent pas encore, une suite se règle à la main, et l'écran le dit.
 *
 * Les boutons dépendent des droits lus dans le jeton, MAIS seule l'API décide : sans le droit, elle refuse, et l'écran
 * affiche son refus.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type ListeSuitesATraiter, type NatureBon, type SuiteATraiter } from '../api/client';
import { useDroitsBons, useDroitsPlanning } from '../auth/useDroits';
import { ErreurFormulaire, Panneau } from '../composants/Formulaire';
import { LIBELLE_ETAT_SUITE, LIBELLE_SUITE, jourFr, libelleMachine } from '../domaine/interventions';
import { activationConvertirIntervention, lienConvertirIntervention, phraseInterventionDeLaSuite } from '../domaine/planning-interventions';
import { natureDeLaSuite } from '../domaine/planning-tournees';
import {
  CHIPS_ETAT_SUITE,
  CHIPS_TYPE_SUITE,
  PHRASE_LECTURE_SEULE,
  TAILLE_PAGE_SUITES,
  TEXTES_GESTE,
  chipEtatParId,
  debutCommentaire,
  filtreDeLaFile,
  peutTrierUneSuite,
  phraseCompte,
  phraseEtatSuite,
  phraseSuccesGeste,
  typeParId,
  validerRaison,
  type GesteSuite,
} from '../domaine/suites';

/** La pastille d'état d'une suite : ouverte à traiter (orange), écartée (grise), soldée (verte). */
function classeEtatSuite(statut: SuiteATraiter['status']): string {
  if (statut === 'OPEN') return 'attente';
  if (statut === 'DISMISSED') return 'suspendu';
  return 'actif';
}

export function SuitesPage() {
  const [parametres, setParametres] = useSearchParams();
  const suiteId = parametres.get('suite');
  const etat = chipEtatParId(parametres.get('etat'));
  const type = typeParId(parametres.get('type'));
  const machineFiltree = parametres.get('machine');
  const droits = useDroitsBons();
  const droitsPlanning = useDroitsPlanning();
  // Les natures servent à proposer celle de la visite (Entretien pour une révision, Dépannage pour un retour) ; lecture seule, sans bloquer l'écran.
  const [natures, setNatures] = useState<readonly NatureBon[]>([]);
  useEffect(() => {
    let annule = false;
    api
      .naturesBon()
      .then((n) => {
        if (!annule) setNatures(n);
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, []);

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  const [rechercheEffective, setRechercheEffective] = useState(recherche);
  const [liste, setListe] = useState<ListeSuitesATraiter | null>(null);
  const [plus, setPlus] = useState<readonly SuiteATraiter[]>([]);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [libelleFiltre, setLibelleFiltre] = useState<string | null>(null);

  const [geste, setGeste] = useState<GesteSuite | null>(null);
  const [raison, setRaison] = useState('');
  const [erreurRaison, setErreurRaison] = useState<ApiError | Error | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /** Incrémenté pour relire la liste après un geste. */
  const [rechargement, setRechargement] = useState(0);

  /** Écrit l'état de l'écran dans l'URL, sans encombrer l'historique. */
  const majUrl = useCallback(
    (changement: Record<string, string | null>) => {
      const p = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changement)) {
        if (valeur === null || valeur === '') p.delete(cle);
        else p.set(cle, valeur);
      }
      setParametres(p, { replace: true });
    },
    [parametres, setParametres],
  );

  // La recherche part après une pause de frappe, et s'écrit dans l'URL.
  useEffect(() => {
    const minuteur = window.setTimeout(() => {
      setRechercheEffective(recherche.trim());
    }, 250);
    return () => window.clearTimeout(minuteur);
  }, [recherche]);

  useEffect(() => {
    if ((parametres.get('q') ?? '') !== rechercheEffective) majUrl({ q: rechercheEffective });
    // `parametres` change à chaque écriture d'URL : seule une nouvelle recherche doit déclencher cette écriture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheEffective]);

  // La liste suit la recherche, l'état, le type et la machine ; elle repart de zéro à chaque changement.
  useEffect(() => {
    let annule = false;
    api
      .suitesATraiter({
        ...filtreDeLaFile({ q: rechercheEffective, etat, type, take: TAILLE_PAGE_SUITES }),
        ...(machineFiltree ? { assetId: machineFiltree } : {}),
      })
      .then((l) => {
        if (annule) return;
        setListe(l);
        setPlus([]);
        setErreurListe(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurListe(e instanceof ApiError ? e.message : 'Chargement des suites impossible.');
      });
    return () => {
      annule = true;
    };
  }, [rechercheEffective, etat, type, machineFiltree, rechargement]);

  // La machine filtrée : son libellé, pour le dire sur le filtre.
  useEffect(() => {
    if (!machineFiltree) {
      setLibelleFiltre(null);
      return;
    }
    let annule = false;
    api
      .asset(machineFiltree)
      .then((m) => {
        if (!annule) setLibelleFiltre(m.label ?? ([m.model, m.serialNumber].filter(Boolean).join(' ') || 'Machine'));
      })
      .catch(() => {
        if (!annule) setLibelleFiltre('cette machine');
      });
    return () => {
      annule = true;
    };
  }, [machineFiltree]);

  const toutes: readonly SuiteATraiter[] = useMemo(() => [...(liste?.items ?? []), ...plus], [liste, plus]);
  const choisie = toutes.find((s) => s.id === suiteId) ?? null;

  // Rien n'est choisi (ou la suite choisie a quitté la liste, réglée sous le filtre « À traiter ») : on lit la première
  // plutôt qu'une colonne vide.
  useEffect(() => {
    const premiere = toutes[0];
    if (premiere && !choisie) majUrl({ suite: premiere.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toutes, choisie]);

  const rafraichir = () => setRechargement((n) => n + 1);

  const selectionner = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ suite: id });
  };

  const choisirEtat = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ etat: id === 'a-traiter' ? null : id, suite: null });
  };

  const choisirType = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ type: id === 'tous' ? null : id, suite: null });
  };

  const chargerPlus = async () => {
    try {
      const l = await api.suitesATraiter({
        ...filtreDeLaFile({ q: rechercheEffective, etat, type, take: TAILLE_PAGE_SUITES, skip: toutes.length }),
        ...(machineFiltree ? { assetId: machineFiltree } : {}),
      });
      setPlus((p) => [...p, ...l.items]);
    } catch (e) {
      setErreurListe(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  };

  const ouvrirGeste = (g: GesteSuite) => {
    setSucces(null);
    setErreurAction(null);
    setErreurRaison(null);
    setRaison('');
    setGeste(g);
  };

  const fermerGeste = () => {
    setGeste(null);
    setErreurRaison(null);
  };

  /**
   * Écarte ou clôt la suite choisie. La raison est contrôlée AVANT l'envoi ; le succès est annoncé APRÈS la relecture ;
   * un refus d'état (déjà réglée, modifiée depuis) ferme le panneau, relit la liste PUIS pose son message dans l'en-tête,
   * là où l'on regarde (l'erreur est posée après la relecture, que celle-ci n'efface pas).
   */
  const confirmer = async () => {
    if (!choisie || !geste) return;
    const lue = validerRaison(raison);
    if (!lue.ok) {
      setErreurRaison(new Error(lue.message));
      return;
    }
    setErreurRaison(null);
    setEnCours(true);
    try {
      const resultat = await (geste === 'ecarter'
        ? api.ecarterSuite(choisie.id, choisie.revision, lue.raison)
        : api.cloreSuite(choisie.id, choisie.revision, lue.raison));
      fermerGeste();
      setErreurAction(null);
      setSucces(phraseSuccesGeste(geste, resultat));
      rafraichir();
    } catch (e) {
      const erreur = e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.');
      if (erreur instanceof ApiError && erreur.status === 409) {
        // La suite a changé sous nos yeux : on relit, et le refus s'affiche dans l'en-tête.
        fermerGeste();
        setSucces(null);
        rafraichir();
        setErreurAction(erreur);
      } else {
        setErreurRaison(erreur);
      }
    } finally {
      setEnCours(false);
    }
  };

  const messages = (
    <>
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
    </>
  );

  const filtresActifs = rechercheEffective !== '' || etat.id !== 'a-traiter' || type !== null || machineFiltree !== null;
  const peutTrier = choisie !== null && peutTrierUneSuite(choisie, droits);
  const conversionIntervention = choisie
    ? activationConvertirIntervention(choisie, droitsPlanning.peutGererPlanning)
    : { visible: false, actif: false, raison: null };
  const textes = geste ? TEXTES_GESTE[geste] : null;

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Suites à traiter</h1>
          <p>Ce qu’il reste à faire après les interventions : un devis, une pièce, un retour, une surveillance…</p>
        </div>
      </div>

      {!droits.peutSaisirBons && <div className="message info">{PHRASE_LECTURE_SEULE}</div>}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <input
              type="text"
              autoFocus
              aria-label="Rechercher une suite"
              placeholder="Rechercher : commentaire, n° du bon, machine…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <div className="chips-secondaires" role="group" aria-label="Filtrer par état">
              {CHIPS_ETAT_SUITE.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`chip${c.id === etat.id ? ' actif' : ''}`}
                  aria-pressed={c.id === etat.id}
                  onClick={() => choisirEtat(c.id)}
                >
                  {c.libelle}
                </button>
              ))}
            </div>
            <div className="chips-secondaires" role="group" aria-label="Filtrer par type">
              {CHIPS_TYPE_SUITE.map((c) => {
                const actif = c.id === 'tous' ? type === null : c.id === type;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`chip${actif ? ' actif' : ''}`}
                    aria-pressed={actif}
                    onClick={() => choisirType(c.id)}
                  >
                    {c.libelle}
                  </button>
                );
              })}
            </div>
            {machineFiltree && (
              <p className="aide">
                Machine : <strong>{libelleFiltre ?? '…'}</strong>{' '}
                <button type="button" className="secondaire discret" onClick={() => majUrl({ machine: null, suite: null })}>
                  Voir toutes les suites
                </button>
              </p>
            )}
            <div className="gammes-liste-outils">
              <span className="aide">{liste === null ? 'Chargement…' : phraseCompte(toutes.length, liste.total, etat)}</span>
            </div>
          </div>

          <div className="gammes-liste-corps">
            {erreurListe && <div className="message erreur">{erreurListe}</div>}
            {liste === null ? (
              !erreurListe && <div className="vide">Chargement…</div>
            ) : toutes.length === 0 ? (
              <div className="vide">
                {filtresActifs ? (
                  'Aucune suite ne correspond à ces filtres.'
                ) : (
                  <>
                    Aucune suite à traiter. Elles naissent à la validation d’un bon d’intervention (« Quelque chose à prévoir ? »)
                    : voir les <Link to="/interventions">interventions</Link>.
                  </>
                )}
              </div>
            ) : (
              <ul className="parc-lignes">
                {toutes.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`parc-ligne${s.id === suiteId ? ' actif' : ''}`}
                      aria-pressed={s.id === suiteId}
                      onClick={() => selectionner(s.id)}
                    >
                      <div className="parc-ligne-titre">
                        {/* Ce qu'il y a à faire en tête : on choisit la plus urgente sans ouvrir (demande de Benjamin, 2026-10-26). */}
                        <strong>{debutCommentaire(s.comment)}</strong>
                        <span className={`etat ${classeEtatSuite(s.status)}`}>{LIBELLE_ETAT_SUITE[s.status]}</span>
                      </div>
                      <div className="aide">{LIBELLE_SUITE[s.kind]}</div>
                      <div className="aide">{libelleMachine(s.asset)}</div>
                      <div className="aide">
                        Bon {s.intervention.number}
                        {s.intervention.performedOn ? ` du ${jourFr(s.intervention.performedOn)}` : ''}
                        {s.customer ? ` · ${s.customer.name}` : ''}
                      </div>
                    </button>
                  </li>
                ))}
                {liste.total > toutes.length && (
                  <li className="parc-charger-plus">
                    <button type="button" className="secondaire" onClick={() => void chargerPlus()}>
                      Afficher plus
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="gammes-fiche">
          {!choisie && (erreurAction || succes) && <div className="gammes-fiche-entete">{messages}</div>}
          {!choisie && liste !== null && toutes.length === 0 && !(erreurAction || succes) && (
            <div className="carte">
              <div className="vide">
                {filtresActifs
                  ? 'Aucune suite à lire avec ces filtres.'
                  : 'Rien à traiter : aucune suite n’est ouverte. Une suite naît à la validation d’un bon, quand on y note quelque chose à prévoir.'}
              </div>
            </div>
          )}
          {!choisie && liste === null && !erreurListe && <div className="vide">Chargement…</div>}

          {choisie && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>
                    {LIBELLE_SUITE[choisie.kind]}{' '}
                    <span className={`etat ${classeEtatSuite(choisie.status)}`}>{LIBELLE_ETAT_SUITE[choisie.status]}</span>
                  </h2>
                  {peutTrier && (
                    <div className="saisie-ligne">
                      <button className="secondaire" disabled={enCours} onClick={() => ouvrirGeste('ecarter')}>
                        {TEXTES_GESTE.ecarter.bouton}
                      </button>
                      <button disabled={enCours} onClick={() => ouvrirGeste('clore')}>
                        {TEXTES_GESTE.clore.bouton}
                      </button>
                    </div>
                  )}
                </div>
                <p className="aide">{phraseEtatSuite(choisie)}</p>
                {conversionIntervention.visible && (
                  <div className="saisie-ligne suite-visite">
                    {conversionIntervention.actif ? (
                      <Link className="bouton-lien" to={lienConvertirIntervention(choisie, natureDeLaSuite(choisie.kind, natures))}>
                        Convertir en intervention
                      </Link>
                    ) : (
                      <button type="button" disabled title={conversionIntervention.raison ?? undefined}>
                        Convertir en intervention
                      </button>
                    )}
                    {choisie.treatedBy && (
                      <>
                        <strong>{phraseInterventionDeLaSuite(choisie.treatedBy)}</strong>
                        <Link className="bouton-lien secondaire-lien" to={`/planning/interventions?intervention=${choisie.treatedBy.id}`}>
                          Ouvrir l’intervention
                        </Link>
                      </>
                    )}
                  </div>
                )}
                {conversionIntervention.visible && conversionIntervention.raison && <p className="aide">{conversionIntervention.raison}</p>}
                {messages}
              </div>

              <div className="carte">
                <h2>Ce qu’il y a à faire</h2>
                <div className="carte-corps observations-bon">{choisie.comment}</div>
              </div>

              <div className="carte">
                <div className="carte-corps">
                  <dl className="proprietes">
                    <dt>À faire</dt>
                    <dd>
                      <strong>{choisie.comment}</strong>
                    </dd>
                    <dt>Bon d’intervention</dt>
                    <dd>
                      <Link to={`/interventions?bon=${choisie.intervention.id}`}>{choisie.intervention.number}</Link>
                      {choisie.intervention.performedOn ? ` du ${jourFr(choisie.intervention.performedOn)}` : ''}
                    </dd>
                    <dt>Machine</dt>
                    <dd>
                      <Link to={`/parc?asset=${choisie.asset.id}`}>{libelleMachine(choisie.asset)}</Link>
                      {choisie.asset.archived && <span className="etat revoque"> archivée</span>}
                      {choisie.asset.clientDesignation ? ` (${choisie.asset.clientDesignation})` : ''}
                    </dd>
                    <dt>Client</dt>
                    <dd>{choisie.customer ? choisie.customer.name : <span className="aide">—</span>}</dd>
                    <dt>Site</dt>
                    <dd>
                      {choisie.site ? <Link to={`/sites/${choisie.site.id}`}>{choisie.site.name}</Link> : <span className="aide">—</span>}
                    </dd>
                    <dt>Notée le</dt>
                    <dd>{jourFr(choisie.createdAt)}</dd>
                    {choisie.status !== 'OPEN' && (
                      <>
                        <dt>{choisie.status === 'DISMISSED' ? 'Écartée le' : 'Soldée le'}</dt>
                        <dd>{jourFr(choisie.resolvedAt)}</dd>
                        <dt>Raison</dt>
                        <dd>{choisie.resolutionNote ?? <span className="aide">—</span>}</dd>
                      </>
                    )}
                  </dl>
                  <p className="aide">
                    Le site et le client sont ceux du bon : c’est pour eux que le travail a été fait, même si la machine a changé
                    d’adresse depuis. <Link to={`/suites?machine=${choisie.asset.id}`}>Voir les suites de cette machine</Link>.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {geste && textes && choisie && (
        <Panneau
          titre={textes.titre}
          sousTitre={textes.sousTitre}
          onFermer={fermerGeste}
          actions={
            <>
              <button type="button" className="secondaire" disabled={enCours} onClick={fermerGeste}>
                Ne rien faire
              </button>
              <button
                type="button"
                className={geste === 'ecarter' ? 'danger' : ''}
                disabled={enCours || validerRaison(raison).ok === false}
                onClick={() => void confirmer()}
              >
                {textes.confirmer}
              </button>
            </>
          }
        >
          <p className="aide">
            <strong>{LIBELLE_SUITE[choisie.kind]}</strong> — {debutCommentaire(choisie.comment, 160)}
          </p>
          <div className="champ">
            <label htmlFor="raison-suite">
              {textes.question} <span className="requis" title="Obligatoire"> *</span>
            </label>
            <textarea
              id="raison-suite"
              rows={4}
              maxLength={500}
              autoFocus
              value={raison}
              placeholder={textes.placeholder}
              onChange={(e) => {
                setRaison(e.target.value);
                setErreurRaison(null);
              }}
            />
            <p className="aide">Trois caractères au moins. La raison n’entre pas dans le journal d’audit.</p>
          </div>
          <ErreurFormulaire
            message={erreurRaison?.message ?? null}
            details={erreurRaison instanceof ApiError ? erreurRaison.details : undefined}
          />
        </Panneau>
      )}
    </div>
  );
}
